import { IPropertyEditable, IPropertyPresentable } from "../../../../base/domain/system/edit";
import { HisInformationSetting } from "../../../../base/domain/his";
import { IColorSpecifiedFeature, TextAlignKinds } from "../../../../base/domain/drawing";
import {
  IHistoryRecord,
  IEditBehavior,
  IInputMethodBehavior,
  InputMethodKinds,
  IPageExtendBehavior,
  ITurnedTextStyle,
  INumberStyle,
  INumberRangeAlertBehavior,
  IFocusBehavior,
  IItemsStyle,
  IItemGroupSelectBehavior,
  IHisInformationItemDefaultSelectBehavior,
  HisInformationItemDefaultSelectKinds,
  IHospitalizedHisInformationItemSelectBehavior,
  IUserDefinedInformationKey,
  IUserDefinedItemSelectBehavior,
  TextObjectKinds,
  IDesignatedDoctorInformationSource,
  ICopyLinkedValuesBehavior,
  IObjectNameIdentifier,
  ILinkedObjectSpecifiable,
  TranscribeTargetKinds,
  IQuestionnaire,
  QuestionnaireType,
} from "../../../../documaker/domain/format/objects";
import { IFormatObjectEditable } from "../IFormatObjectEditable";
import { IDesignatedDoctorType, IFormatSpecification, IUserDefinitionSpecification } from "../IFormatSpecification";
import { ITextObject } from "../objects";
import {
  IInformationKeyHistoriesPropertyEditable,
  IEditBehaviorPropertyEditable,
  IInputMethodBehaviorPropertyEditable,
  IPageExtendBehaviorPropertyEditable,
  ITurnedTextStylePropertyEditable,
  INumberStylePropertyEditable,
  INumberRangeAlertBehaviorPropertyEditable,
  IFocusBehaviorPropertyEditable,
  IPaintStyleOnEditingPropertyEditable,
  IItemsStylePropertyEditable,
  IItemGroupSelectBehaviorPropertyEditable,
  IHisInformationItemDefaultSelectBehaviorPropertyEditable,
  IHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
  IUserDefinedInformationKeyPropertyEditable,
  IUserDefinedItemSelectBehaviorPropertyEditable,
  IDesignatedDoctorInformationSourcePropertyEditable,
  ICopyLinkedTextsBehaviorPropertyEditable,
  IQuestionnairePropertyEditable,
} from "../properties";
import { AbstractStandardTextDrawableObjectEditor } from "./AbstractStandardTextDrawableObjectEditor";

export class TextObjectEditor extends AbstractStandardTextDrawableObjectEditor<ITextObject> implements
IInformationKeyHistoriesPropertyEditable,
IEditBehaviorPropertyEditable,
IInputMethodBehaviorPropertyEditable,
IPageExtendBehaviorPropertyEditable,
ITurnedTextStylePropertyEditable,
INumberStylePropertyEditable,
INumberRangeAlertBehaviorPropertyEditable,
IFocusBehaviorPropertyEditable,
IPaintStyleOnEditingPropertyEditable,
IItemsStylePropertyEditable,
IItemGroupSelectBehaviorPropertyEditable,
IHisInformationItemDefaultSelectBehaviorPropertyEditable,
IHospitalizedHisInformationItemSelectBehaviorPropertyEditable,
IUserDefinedInformationKeyPropertyEditable,
IUserDefinedItemSelectBehaviorPropertyEditable,
IDesignatedDoctorInformationSourcePropertyEditable,
ICopyLinkedTextsBehaviorPropertyEditable,
IQuestionnairePropertyEditable {
  constructor(source: ITextObject | TextObjectEditor, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._id = this.createIdPropertyEditor(s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    // nameはv1の@title/@name-sub(v1の表示名はサブ名称)として扱うので常に有効.(指定医番号、関節可動域の場合だけは変更できないので無効.)
    this._name = this.createNamePropertyEditor(s => (s.objectKind !== TextObjectKinds.DesignatedDoctorNumber) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    // nameHistoriesはHIS情報等情報を示す場合は情報キーの方の履歴を記録する.(更新する情報は一緒.UIが別物なので分ける.)
    this._nameHistories = this.createNameHistoriesPropertyEditor(s => !this.isInformationSpecifiable(s));
    this._bookmark = this.createBookmarkPropertyEditor(s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._printBehavior = this.createPropertyEditor(s => s.printBehavior, (s, v) => s.printBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    // 仕様では指定医番号設定できるようになってるがv1は設定できないので無効化.QA確認後正式に対応する事.
    this._quoteBehavior = this.createPropertyEditor(
      s => s.quoteBehavior,
      (s, v) => s.quoteBehavior = v,
      s => (s.objectKind !== TextObjectKinds.RangeOfMotion) && (s.objectKind !== TextObjectKinds.DesignatedDoctorNumber));
    this._freeInputBehavior = this.createPropertyEditor(s => s.freeInputBehavior, (s, v) => s.freeInputBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._naturalTextBehavior = this.createPropertyEditor(s => s.naturalTextBehavior, (s, v) => s.naturalTextBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._textSearchBehavior = this.createPropertyEditor(s => s.textSearchBehavior, (s, v) => s.textSearchBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._groupAlertBehavior = this.createPropertyEditor(s => s.groupAlertBehavior, (s, v) => s.groupAlertBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._emptyAlertBehavior = this.createPropertyEditor(s => s.emptyAlertBehavior, (s, v) => s.emptyAlertBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._lengthAlertBehavior = this.createPropertyEditor(s => s.lengthAlertBehavior, (s, v) => s.lengthAlertBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._tooltipBehavior = this.createPropertyEditor(s => s.tooltipBehavior, (s, v) => s.tooltipBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._textStyle = this.createTextStylePropertyEditor();
    this._fontSizeAutoAdjustBehavior = this.createFontSizeAutoAdjustBehaviorPropertyEditor();
    this._lineBreakBehavior = this.createLineBreakBehaviorPropertyEditor(s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.Number) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._informationKeyHistories = this.createNameHistoriesPropertyEditor(s => this.isInformationSpecifiable(s));
    this._hisInformationKey = this.createInformationKeyPropertyEditor(
      s => (s.objectKind === TextObjectKinds.HisItem),
      (s, v) => {
        const selectedHisInformation = this.findSelectableHisInformation(v);
        if (selectedHisInformation) {
          s.multiLineBehavior.isEnable = (selectedHisInformation.objectType === "TEXTAREA");
        }
      });
    this._userDefinedInformationKey = this.createPropertyEditor(
      s => ({
        name: s.userDefinedInformationSource.fileName,
        column: s.informationKey,
      }),
      (s, v) => {
        s.userDefinedInformationSource.fileName = v.name;
        s.informationKey = v.column;
      },
      s => (s.objectKind === TextObjectKinds.UserDefined));
    this._designatedDoctorInformationSource = this.createPropertyEditor(
      s => s.designatedDoctorInformationSource,
      (s, v) => s.designatedDoctorInformationSource = v,
      s => (s.objectKind === TextObjectKinds.DesignatedDoctorNumber));
    this._text = this.createTextPropertyEditor(this.isWritableText);
    this._editBehavior = this.createPropertyEditor(s => s.editBehavior, (s, v) => s.editBehavior = v, s => (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._inputRuleBehavior = this.createInputRuleBehaviorPropertyEditor(s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.Number) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._inputMethodBehavior = this.createPropertyEditor(
      s => s.inputMethodBehavior,
      (s, v) => s.inputMethodBehavior = v,
      s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.Number) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._pageExtendBehavior = this.createPropertyEditor(
      s => s.pageExtendBehavior,
      (s, v) => s.pageExtendBehavior = v,
      s => (s.objectKind === TextObjectKinds.Free) || (s.objectKind === TextObjectKinds.UserDefined));
    this._turnedTextStyle = this.createPropertyEditor(
      s => s.turnedTextStyle,
      (s, v) => s.turnedTextStyle = v,
      s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.Number) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._numberStyle = this.createPropertyEditor(
      s => s.numberStyle,
      (s, v) => s.numberStyle = v,
      s => (s.objectKind === TextObjectKinds.Number));
    this._numberRangeAlertBehavior = this.createPropertyEditor(
      s => s.numberRangeAlertBehavior,
      (s, v) => s.numberRangeAlertBehavior = v,
      s => (s.objectKind === TextObjectKinds.Number));
    this._focusBehavior = this.createPropertyEditor(
      s => s.focusBehavior,
      (s, v) => s.focusBehavior = v,
      s => (s.objectKind === TextObjectKinds.Fixed));
    this._paintStyleOnEditing = this.createPropertyEditor(
      s => s.paintStyleOnEditing,
      (s, v) => s.paintStyleOnEditing = v,
      s => (s.objectKind === TextObjectKinds.Fixed));
    this._itemsStyle = this.createPropertyEditor(
      s => s.itemsStyle,
      (s, v) => s.itemsStyle = v,
      s => {
        const selectedHisInfo = this.getSelectedHisInformation();
        return (s.objectKind === TextObjectKinds.UserDefined)
          || (s.objectKind === TextObjectKinds.DesignatedDoctorNumber)
          || ((s.objectKind === TextObjectKinds.HisItem) && !!selectedHisInfo && (selectedHisInfo.objectType === "TEXTAREA"));
      });
    this._itemGroupSelectBehavior = this.createPropertyEditor(
      s => s.itemGroupSelectBehavior,
      (s, v) => s.itemGroupSelectBehavior = v,
      s => (s.objectKind === TextObjectKinds.HisItem) || (s.objectKind === TextObjectKinds.UserDefined) || (s.objectKind === TextObjectKinds.DesignatedDoctorNumber));
    this._hisInformationItemDefaultSelectBehavior = this.createPropertyEditor(
      s => s.hisInformationItemDefaultSelectBehavior,
      (s, v) => s.hisInformationItemDefaultSelectBehavior = v,
      s => (s.objectKind === TextObjectKinds.HisItem));   // @initialはv1だとHIS項目以外でも設定できるが見てないらしい.設定できないようにする.
    this._hospitalizedHisInformationItemSelectBehavior = this.createPropertyEditor(
      s => s.hospitalizedHisInformationItemSelectBehavior,
      (s, v) => s.hospitalizedHisInformationItemSelectBehavior = v,
      s => {
        const selectedHisInfo = this.getSelectedHisInformation();
        return (s.objectKind === TextObjectKinds.HisItem) && !!selectedHisInfo && (selectedHisInfo.itemType === "HOSPITALIZATION");
      });
    this._userDefinedItemSelectBehavior = this.createPropertyEditor(
      s => s.userDefinedItemSelectBehavior,
      (s, v) => s.userDefinedItemSelectBehavior = v,
      s => (s.objectKind === TextObjectKinds.UserDefined));
    this._copyLinkedTextsBehavior = this.createPropertyEditor(
      s => s.copyLinkedTextsBehavior,
      (s, v) => s.copyLinkedTextsBehavior = v,
      s => (s.objectKind === TextObjectKinds.Fixed));
    this._rangeOfMotionGroupKey = this.createPropertyEditor(
      s => s.informationKey,
      (s, v) => s.informationKey = v,
      s => (s.objectKind === TextObjectKinds.RangeOfMotion));
    this._objectKind = this.createPropertyPresenter(s => s.objectKind);
    this._transcribeBehavior = this.createTranscribeBehaviorPropertyEditor(s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._scriptExecuteTriggerBehavior = this.createScriptExecuteTriggerBehaviorPropertyEditor(s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._emptyAlertAllowBehavior = this.createEmptyAlertAllowBehaviorPropertyEditor(s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._groupAlertAllowBehavior = this.createGroupAlertAllowBehaviorPropertyEditor(s => (s.objectKind !== TextObjectKinds.Fixed) && (s.objectKind !== TextObjectKinds.RangeOfMotion));
    this._questionnaire = this.createPropertyEditor(s => s.questionnaire, (s, v) => s.questionnaire = v);
  }

  getSelectableHisInformations(): HisInformationSetting[] {
    return this._specification.objects.text.hisInformations;
  }

  getSelectableUserDefinedInformations(): IUserDefinitionSpecification[] {
    return this._specification.userDefinitions;
  }

  protected _informationKeyHistories: IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]>;
  protected _editBehavior: IPropertyEditable<IEditBehavior>;
  protected _inputMethodBehavior: IPropertyEditable<IInputMethodBehavior>;
  protected _pageExtendBehavior: IPropertyEditable<IPageExtendBehavior>;
  protected _turnedTextStyle: IPropertyEditable<ITurnedTextStyle>;
  protected _numberStyle: IPropertyEditable<INumberStyle>;
  protected _numberRangeAlertBehavior: IPropertyEditable<INumberRangeAlertBehavior>;
  protected _focusBehavior: IPropertyEditable<IFocusBehavior>;
  protected _paintStyleOnEditing: IPropertyEditable<IColorSpecifiedFeature>;
  protected _itemsStyle: IPropertyEditable<IItemsStyle>;
  protected _itemGroupSelectBehavior: IPropertyEditable<IItemGroupSelectBehavior>;
  protected _hisInformationItemDefaultSelectBehavior: IPropertyEditable<IHisInformationItemDefaultSelectBehavior>;
  protected _hospitalizedHisInformationItemSelectBehavior: IPropertyEditable<IHospitalizedHisInformationItemSelectBehavior>;
  protected _userDefinedInformationKey: IPropertyEditable<IUserDefinedInformationKey>;
  protected _userDefinedItemSelectBehavior: IPropertyEditable<IUserDefinedItemSelectBehavior>;
  protected _designatedDoctorInformationSource: IPropertyEditable<IDesignatedDoctorInformationSource>;
  protected _copyLinkedTextsBehavior: IPropertyEditable<ICopyLinkedValuesBehavior>;
  protected _rangeOfMotionGroupKey: IPropertyEditable<string>;
  protected _objectKind: IPropertyPresentable<TextObjectKinds>;
  protected _questionnaire: IPropertyEditable<IQuestionnaire>;

  informationKeyHistories(): IPropertyEditable<IHistoryRecord<IObjectNameIdentifier>[]> {
    return this._informationKeyHistories;
  }

  userDefinedInformationKey(): IPropertyEditable<IUserDefinedInformationKey> {
    return this._userDefinedInformationKey;
  }

  designatedDoctorInformationSource(): IPropertyEditable<IDesignatedDoctorInformationSource> {
    return this._designatedDoctorInformationSource;
  }

  editBehavior(): IPropertyEditable<IEditBehavior> {
    return this._editBehavior;
  }

  inputMethodBehavior(): IPropertyEditable<IInputMethodBehavior> {
    return this._inputMethodBehavior;
  }

  getSelectableInputMethods(): InputMethodKinds[] {
    return this._specification.objects.text.inputMethods;
  }

  pageExtendBehavior(): IPropertyEditable<IPageExtendBehavior> {
    return this._pageExtendBehavior;
  }

  turnedTextStyle(): IPropertyEditable<ITurnedTextStyle> {
    return this._turnedTextStyle;
  }

  numberStyle(): IPropertyEditable<INumberStyle> {
    return this._numberStyle;
  }

  numberRangeAlertBehavior(): IPropertyEditable<INumberRangeAlertBehavior> {
    return this._numberRangeAlertBehavior;
  }

  focusBehavior(): IPropertyEditable<IFocusBehavior> {
    return this._focusBehavior;
  }

  paintStyleOnEditing(): IPropertyEditable<IColorSpecifiedFeature> {
    return this._paintStyleOnEditing;
  }

  itemsStyle(): IPropertyEditable<IItemsStyle> {
    return this._itemsStyle;
  }

  itemGroupSelectBehavior(): IPropertyEditable<IItemGroupSelectBehavior> {
    return this._itemGroupSelectBehavior;
  }

  hisInformationItemDefaultSelectBehavior(): IPropertyEditable<IHisInformationItemDefaultSelectBehavior> {
    return this._hisInformationItemDefaultSelectBehavior;
  }

  getSelectableHisInformationItemDefaultSelectKinds(): HisInformationItemDefaultSelectKinds[] {
    return this._specification.objects.text.hisInformationItemDefaultSelectKinds;
  }

  hospitalizedHisInformationItemSelectBehavior(): IPropertyEditable<IHospitalizedHisInformationItemSelectBehavior> {
    return this._hospitalizedHisInformationItemSelectBehavior;
  }

  userDefinedItemSelectBehavior(): IPropertyEditable<IUserDefinedItemSelectBehavior> {
    return this._userDefinedItemSelectBehavior;
  }

  copyLinkedTextsBehavior(): IPropertyEditable<ICopyLinkedValuesBehavior> {
    return this._copyLinkedTextsBehavior;
  }

  rangeOfMotionGroupKey(): IPropertyEditable<string> {
    return this._rangeOfMotionGroupKey;
  }

  objectKind(): IPropertyPresentable<TextObjectKinds> {
    return this._objectKind;
  }

  questionnaire(): IPropertyEditable<IQuestionnaire> {
    return this._questionnaireTypes[this.objectKind().getValue()]
      ? this._questionnaire
      : undefined;
  }

  selectableQuestionnaireTypes(): (QuestionnaireType | undefined)[] {
    const types = this._questionnaireTypes[this.objectKind().getValue()];
    return types
      ? types
      : [];
  }

  private readonly _questionnaireTypes: (QuestionnaireType | undefined)[][] = [
    [ // TextObjectKinds.Free
      undefined,
      "text",
      "string",
    ],
    [ // TextObjectKinds.Fixed
      undefined,
      "display",
      "group",
    ],
    [ // TextObjectKinds.Number
      undefined,
      "decimal",
      "integer",
    ],
    // 他は対象外.
  ];

  private isInformationSpecifiable(s: ITextObject): boolean {
    return (s.objectKind === TextObjectKinds.HisItem)
        || (s.objectKind === TextObjectKinds.UserDefined)
        || (s.objectKind === TextObjectKinds.DesignatedDoctorNumber);
  }

  private isWritableText(s: ITextObject): boolean {
    switch (s.objectKind) {
      case TextObjectKinds.Free:
      case TextObjectKinds.Fixed:
      case TextObjectKinds.Number:
        return true;
      default:
        return false;
    }
  }

  getTypeName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.Free:
        return "フリー入力テキスト";
      case TextObjectKinds.Fixed:
        return "固定テキスト";
      case TextObjectKinds.Number:
        return "数値";
      case TextObjectKinds.HisItem:
        return "HIS項目テキスト";
      case TextObjectKinds.UserDefined:
        return "ユーザ定義テキスト";
      case TextObjectKinds.DesignatedDoctorNumber:
        return "指定医番号";
      case TextObjectKinds.RangeOfMotion:
        return "関節可動域";
      default:
        return "テキスト";
    }
  }

  getIdentifiableName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.HisItem:
        return this.getIdentifiableHisName();
      case TextObjectKinds.RangeOfMotion:
        return this.rangeOfMotionGroupKey().getValue();
      case TextObjectKinds.UserDefined:
      case TextObjectKinds.DesignatedDoctorNumber:
      case TextObjectKinds.Free:
      case TextObjectKinds.Fixed:
      case TextObjectKinds.Number:
      default:
        return this.name().getValue();
    }
  }

  toLinkedObjectIdentifier(): ILinkedObjectSpecifiable {
    return {
      type: this.objectTypeName().getValue(),
      id: this.id().getValue(),
      key: this.getLinkedObjectKey(),
    };
  }

  private getLinkedObjectKey(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.HisItem:
        return this.hisInformationKey().getValue();
      case TextObjectKinds.UserDefined:
        return this.userDefinedInformationKey().getValue().column;
      case TextObjectKinds.DesignatedDoctorNumber:
        return "指定医番号";
      default:
        return this.name().getValue();
    }
  }

  getSelectableAlignmentKinds(): TextAlignKinds[] {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.HisItem:
        return this._specification.objects.text.textObjects.hisText.alignmentKinds;
      case TextObjectKinds.UserDefined:
        return this._specification.objects.text.textObjects.userDefinedItem.alignmentKinds;
      case TextObjectKinds.DesignatedDoctorNumber:
        ​return this._specification.objects.text.textObjects.designatedDoctorNumberText.alignmentKinds;
      case TextObjectKinds.Free:
        return this._specification.objects.text.textObjects.freeText.alignmentKinds;
      case TextObjectKinds.Fixed:
        return this._specification.objects.text.textObjects.fixedText.alignmentKinds;
      case TextObjectKinds.Number:
        return this._specification.objects.text.textObjects.numberText.alignmentKinds;
      case TextObjectKinds.RangeOfMotion:
        return this._specification.objects.text.textObjects.rangeOfMotionText.alignmentKinds;
      default:
        return null;
    }
  }

  getWarningName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.HisItem:
        return this.getHisInformationName();
      case TextObjectKinds.Number:
        return this.name().getValue();
      default: return "";
    }
  }

  getWarningSettingName(): string {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.HisItem:
        return this.hisInformationKey().getValue();
      case TextObjectKinds.Number:
        return this.name().getValue();
      default: return "";
    }
  }

  isSettableWarningName(): boolean {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.HisItem:
      case TextObjectKinds.Number:
        return true;
      default: return false;
    }
  }
  
  getSelectableDesignatedDoctorTypes(): IDesignatedDoctorType[] {
    const objectKind = this._objectKind.getValue();
    switch (objectKind) {
      case TextObjectKinds.DesignatedDoctorNumber:
        return this._specification.designatedDoctorInformations;
      default:
        return [];
    }
  }

  getSelectableTranscribeTargets(): TranscribeTargetKinds[] {
    return this._specification.objects.text.transcription.targets;
  }

  clone(): IFormatObjectEditable {
    return new TextObjectEditor(this);
  }
}
